import React, { useEffect, useState } from "react";

interface AiChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const AI_API_URL = process.env.REACT_APP_AI_API_URL;

export const AiChatModal: React.FC<AiChatModalProps> = ({ isOpen, onClose }) => {
  const [selectedDays, setSelectedDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Dummy data structure matching backend: (total_messages, per_day_counts, engagement_generated)
  const DUMMY_CHAT_DATA = {
    1: { 
      total: 1247, 
      engagement_generated: 50,
      history: [["2024-01-20", 1247]] 
    },
    7: { 
      total: 8934, 
      engagement_generated: 50,
      history: [
        ["2024-01-14", 1186],
        ["2024-01-15", 1432],
        ["2024-01-16", 1298],
        ["2024-01-17", 1576],
        ["2024-01-18", 967],
        ["2024-01-19", 1228],
        ["2024-01-20", 1247]
      ]
    },
    14: { 
      total: 17823, 
      engagement_generated: 50,
      history: [
        ["2024-01-07", 1203], ["2024-01-08", 1345], ["2024-01-09", 1456], ["2024-01-10", 1678], 
        ["2024-01-11", 1089], ["2024-01-12", 1234], ["2024-01-13", 1398], ["2024-01-14", 1186],
        ["2024-01-15", 1432], ["2024-01-16", 1298], ["2024-01-17", 1576], ["2024-01-18", 967],
        ["2024-01-19", 1228], ["2024-01-20", 1247]
      ]
    },
    30: { 
      total: 36127, 
      engagement_generated: 50,
      history: [
        ["2024-01-01", 1156], ["2024-01-02", 1289], ["2024-01-03", 1023], ["2024-01-04", 1467],
        ["2024-01-05", 891], ["2024-01-06", 1334], ["2024-01-07", 1203], ["2024-01-08", 1345],
        ["2024-01-09", 1456], ["2024-01-10", 1678], ["2024-01-11", 1089], ["2024-01-12", 1234],
        ["2024-01-13", 1398], ["2024-01-14", 1186], ["2024-01-15", 1432], ["2024-01-16", 1298],
        ["2024-01-17", 1576], ["2024-01-18", 967], ["2024-01-19", 1228], ["2024-01-20", 1247],
        ["2024-01-21", 1312], ["2024-01-22", 1198], ["2024-01-23", 1445], ["2024-01-24", 1623],
        ["2024-01-25", 1067], ["2024-01-26", 1389], ["2024-01-27", 1245], ["2024-01-28", 1356],
        ["2024-01-29", 1178], ["2024-01-30", 1401]
      ]
    }
  };

  const [hoveredBar, setHoveredBar] = useState<{index: number, count: number, date: string} | null>(null);

//   useEffect(() => {
//     if (isOpen) {
//       setLoading(true);
//       setTimeout(() => {
//         setData(DUMMY_CHAT_DATA[selectedDays as keyof typeof DUMMY_CHAT_DATA]);
//         setLoading(false);
//       }, 800);
//     }
//   }, [isOpen, selectedDays]);


    useEffect(() => {
    if (!isOpen) return () => {};

    const controller = new AbortController();
    setLoading(true);

    fetch(
        `${AI_API_URL}/fetch-ai-chat-stats?days=${selectedDays}`,
        { signal: controller.signal }
    )
        .then((r) => {
        if (!r.ok) throw new Error("AI Chat fetch failed");
        return r.json();
        })
        .then((json) => {
        setData(json);
        })
        .catch((err) => {
        if (err.name !== "AbortError") console.error(err);
        })
        .finally(() => setLoading(false));

    return () => controller.abort();
    }, [isOpen, selectedDays]);

  const maxValue = data ? Math.max(...data.history.map((item: any) => item[1])) : 0;

  if (!isOpen) return null;

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(4px)",
        animation: isOpen ? "fadeIn 0.3s ease-out" : "fadeOut 0.3s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      }}
      tabIndex={-1}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: "20px",
          padding: "32px",
          width: "90%",
          maxWidth: "800px",
          maxHeight: "90vh",
          overflowY: "auto",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          animation: isOpen ? "zoomIn 0.4s cubic-bezier(0.4, 0, 0.2, 1)" : "",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
          <div>
            <h2 style={{ color: "#ffffff", fontSize: "24px", fontWeight: "700", margin: 0 }}>
              AI Chat Analytics
            </h2>
            <p style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px", margin: "4px 0 0 0" }}>
              Message volume and engagement metrics
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "8px",
              color: "#ffffff",
              padding: "8px 12px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            ✕
          </button>
        </div>

        {/* Controls - Only Time Period */}
        <div style={{ marginBottom: "24px", maxWidth: "250px" }}>
          <p style={{ margin: 0, marginBottom: 8, color: "rgba(255, 255, 255, 0.8)", fontSize: 14, fontWeight: 500 }}>
            Time Period
          </p>
          <select
            id="chat-period"
            aria-label="Time period"
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            style={{
              display: "block",
              width: "100%",
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 8,
              color: "#ffffff",
              padding: "8px 12px",
              fontSize: 14,
              outline: "none",
            }}
          >
            <option value={1} style={{ background: "#1f2937", color: "#ffffff" }}>Last 24 hours</option>
            <option value={7} style={{ background: "#1f2937", color: "#ffffff" }}>Last 7 days</option>
            <option value={14} style={{ background: "#1f2937", color: "#ffffff" }}>Last 14 days</option>
            <option value={30} style={{ background: "#1f2937", color: "#ffffff" }}>Last 30 days</option>
          </select>
        </div>

        {/* Chart */}
        <div style={{ marginBottom: "32px" }}>
          <h3 style={{ color: "#ffffff", fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>
            Daily Message Volume
          </h3>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "12px",
              padding: "24px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              height: "300px",
              display: "flex",
              position: "relative",
            }}
          >
            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", color: "rgba(255, 255, 255, 0.7)" }}>
                Loading chart...
              </div>
            ) : (
              <>
                {/* Y-Axis */}
                <div
                  style={{
                    width: "60px",
                    height: "240px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    paddingRight: "12px",
                    marginRight: "16px",
                    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                  }}
                >
                  {[...Array(6)].map((_, i) => {
                    const value = Math.round((maxValue * (5 - i)) / 5);
                    return (
                      <div
                        key={i}
                        style={{
                          color: "rgba(255, 255, 255, 0.6)",
                          fontSize: "11px",
                          fontWeight: "500",
                          textAlign: "right",
                          lineHeight: "1",
                        }}
                      >
                        {value > 0 ? value.toLocaleString() : 0}
                      </div>
                    );
                  })}
                </div>

                {/* Chart Area */}
                <div
                  style={{
                    flex: 1,
                    height: "240px",
                    display: "flex",
                    alignItems: "end",
                    justifyContent: "space-around",
                    gap: "4px",
                    position: "relative",
                  }}
                >
                  {/* Horizontal grid lines */}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: `${(i * 240) / 5}px`,
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.05)",
                        pointerEvents: "none",
                      }}
                    />
                  ))}

                {/* Bars */}
                {data?.history.map((item: any, index: number) => {
                const [date, count] = item;
                const height = (count / maxValue) * 240;

                return (
                    <div
                    key={date}
                    style={{
                        position: "relative",
                        flex: 1,
                        height: "100%",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                    }}
                    >
                    <div
                        style={{
                        height: `${height}px`,
                        width: "100%",
                        maxWidth: 40,
                        background: "linear-gradient(to top, #10b981, #34d399)",
                        borderRadius: "4px 4px 0 0",
                        boxShadow: "0 4px 12px rgba(16,185,129,0.3)",
                        animation: `barGrowUp 1s ease-out ${index * 0.1}s both`,
                        cursor: "pointer",
                        }}
                        onMouseEnter={() => setHoveredBar({ index, count, date })}
                        onMouseLeave={() => setHoveredBar(null)}
                    />

                    {hoveredBar?.index === index && (
                        <div
                        style={{
                            position: "absolute",
                            bottom: `${height + 10}px`,
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "rgba(0,0,0,.8)",
                            color: "#34d399",
                            padding: "4px 8px",
                            borderRadius: 4,
                            fontSize: 12,
                            fontWeight: 600,
                            pointerEvents: "none",
                        }}
                        >
                        {count.toLocaleString()}
                        </div>
                    )}

                    <div
                        style={{
                        position: "absolute",
                        bottom: -18,
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "rgba(255,255,255,.6)",
                        fontSize: 10,
                        lineHeight: 1.2,
                        textAlign: "center",
                        whiteSpace: "nowrap",
                        }}
                    >
                        {new Date(date).toLocaleDateString([], { month: "short", day: "numeric" })}
                    </div>
                    </div>
                );
                })}

                </div>
              </>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div style={{ color: "#34d399", fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
              {data?.total.toLocaleString() || 0}
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Total Messages</div>
          </div>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.08)",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              textAlign: "center",
            }}
          >
            <div 
              style={{ 
                color: "#34d399", 
                fontSize: "28px", 
                fontWeight: "700", 
                marginBottom: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px"
              }}
            >
              +{data?.engagement_generated || 0}%
              <span style={{ fontSize: "18px" }}>📈</span>
            </div>
            <div style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>Engagement Generated</div>
          </div>
        </div>
      </div>
    </div>
  );
};